/**
 * Mensajes para el flujo de eliminación de pedidos premium.
 * Tono cercano (tú), sin perder respeto: impacto en ventas, metas y utilidad;
 * invitación a agotar alternativas antes de borrar; aviso de que dirección
 * entera del movimiento. Estructura alineada con {@link CART_MESSAGES}.
 */
export const PREMIUM_ORDER_DELETION_CONFIRMATION_TOASTS = [
    {
        title: '📊 ¿Dar de baja este pedido?',
        message:
            '💼 Cada pedido en cartera es una oportunidad de ingreso. Si lo eliminas, esa venta deja de sumar al objetivo del mes y puede pegarle a la utilidad. ¿Ya viste si podías concretarla de otra forma? Cuando confirmes, 📣 le avisan a dirección.',
        buttonTrue: '🗑️ Sí, eliminar pedido',
        buttonFalse: '↩️ Mejor lo dejo y sigo trabajándolo'
    },
    {
        title: '🎯 ¿Tirar una oportunidad comercial?',
        message:
            '📉 Las metas del mes se arman pedido a pedido. Quitar este registro es asumir una venta perdida que en este periodo ya no cierra. Si todavía no probaste todos los canales para salvar el negocio, frena un segundo. ⚠️ La decisión queda registrada y dirección recibe aviso.',
        buttonTrue: 'Eliminar igual',
        buttonFalse: 'Dejar el pedido activo'
    },
    {
        title: '💰 ¿Mandar a la basura ingreso potencial?',
        message:
            '🏢 La rentabilidad del equipo se nota en cada cierre. Borrar este pedido achica el pipeline y te puede alejar de la meta de ventas del mes. Sigue solo si, después de intentar cerrar por todos lados razonables, no te queda otra. 📋 Al ejecutar, le avisan a dirección.',
        buttonTrue: '🗑️ Confirmar eliminación',
        buttonFalse: '↺ Volver atrás, sin borrar'
    },
    {
        title: '📉 Un paso que achica el pipeline',
        message:
            '🧭 Este pedido sumaba a tu forecast. Borrarlo es reconocer una venta que no se va a materializar y cambia el panorama. Si ya agotaste seguimiento, logística y lo que se te ocurrió, adelante; si no, quizá convenga dejarlo. 🔔 Dirección se entera.',
        buttonTrue: 'Sí, eliminar',
        buttonFalse: 'Cancelar — todavía hay margen'
    },
    {
        title: '⚖️ Esto pesa en los resultados',
        message:
            '📆 Cerrar el mes bien depende de cada oportunidad viva. Eliminar este pedido es sacar una venta del tablero y puede pegarle a la utilidad del área. Por transparencia, ✅ cuando confirmas avisan a dirección sobre la baja.',
        buttonTrue: '🗑️ Eliminar el pedido',
        buttonFalse: 'Lo conservo — quiero intentar el cierre'
    },
    {
        title: '🛑 ¿Última palabra sobre este pedido?',
        message:
            '💡 Antes de borrar: ¿intentaste recuperar el negocio con el cliente, ajustar condiciones o pedir una mano al equipo? Una venta perdida acá no vuelve al mismo mes. Si después de eso la baja es inevitable, al confirmar ten presente que 📣 dirección recibe el parte.',
        buttonTrue: 'Eliminar',
        buttonFalse: 'Mejor lo dejo activo'
    },
    {
        title: '📋 ¿Archivas una venta que aún podría cerrarse?',
        message:
            '🌐 En la cancha, cada pedido suma a la meta. Sacarlo del sistema es aceptar que en esta gestión no entra ingreso de ahí, y eso se nota en los números y en cómo se ve el equipo. 🔔 Le avisan a dirección.',
        buttonTrue: '🗑️ Dar de baja',
        buttonFalse: 'Seguir gestionándolo'
    },
    {
        title: '💼 Ojo con el forecast',
        message:
            '📈 Las proyecciones viven de pedidos reales. Si eliminas este, le quitas una pieza al plan del mes y puedes desalinear metas y márgenes. Confirma solo si ya no hay ruta después de agotar opciones. ⚠️ Dirección se entera cuando ejecutas.',
        buttonTrue: 'Confirmar baja',
        buttonFalse: 'No eliminar'
    },
    {
        title: '🤝 ¿Le cierras la puerta a esta venta?',
        message:
            '🔑 Toda baja es plata que no se concretó. Si todavía no recorriste todos los medios para salvar el pedido, tal vez vale otra charla con el cliente o con el equipo. Si la decisión es firme, ten presente que 📬 dirección se entera de la eliminación.',
        buttonTrue: '🗑️ Eliminar',
        buttonFalse: 'Darle una vuelta más al caso'
    },
    {
        title: '📌 Nada de esto es invisible',
        message:
            '🏛️ Las eliminaciones impactan reportes, metas y la confianza en el pipeline. Cada pedido borrado es venta que no suma al mes y puede afectar la utilidad que proyectabas. Al confirmar, ✅ avisan a dirección para que todos vayan alineados.',
        buttonTrue: 'Entiendo, eliminar',
        buttonFalse: 'Prefiero no eliminar'
    },
    {
        title: '⏳ ¿Seguro que este pedido no tiene más vida?',
        message:
            '🔄 A veces con una llamada o un ajuste salvas la situación. Si ya probaste eso y la baja es lo único que cierra, puedes seguir. Acuérdate: 📢 dirección se entera y el pedido deja de contar en tus indicadores.',
        buttonTrue: '🗑️ Sí, eliminar',
        buttonFalse: 'Mejor lo dejo — todavía hay opciones'
    }
];

/**
 * Mensajes con un poco más de énfasis en impacto y trazabilidad.
 * Sirven como segunda capa o para rotar con el array principal.
 */
export const PREMIUM_ORDER_DELETION_STRATEGIC_WARNING_TOASTS = [
    {
        title: '⚠️ Esto se nota en el tablero',
        message:
            '📉 Eliminar este pedido es registrar una venta perdida: deja de contar para el objetivo del mes y puede comerse utilidad que esperabas. Confirma solo si ya exploraste alternativas razonables para concretar. 🔕 Al ejecutar, dirección recibe la notificación.',
        buttonTrue: 'Eliminar igual',
        buttonFalse: 'Mejor no — lo reconsidero'
    },
    {
        title: '🏦 ¿Le sacas una pieza al plan de ingresos?',
        message:
            '💹 El mes se arma con cierres, no con bajas. Este pedido, aunque esté difícil, seguía siendo oportunidad; sacarlo baja el techo de ventas que puedes alcanzar. Si no te queda margen después de intentar cerrar por todos lados, adelante. 📋 Dirección se entera.',
        buttonTrue: '🗑️ Sí, eliminar',
        buttonFalse: 'Dejarlo en pipeline'
    },
    {
        title: '📢 La decisión queda registrada',
        message:
            '🔍 Borrar pedidos es delicado: mueve forecast, metas y cómo se lee el desempeño del equipo. Cada baja es venta que en este mes no vuelves a recuperar. Por eso, ✅ dirección se entera cuando confirmas.',
        buttonTrue: 'Confirmar eliminación',
        buttonFalse: 'Cancelar'
    },
    {
        title: '🧭 ¿Ya no hay camino al cierre?',
        message:
            '💼 Antes de borrar, piensa si agotaste seguimiento, negociación y ayuda adentro. Eliminar de más puede costarte ingreso y reputación. Si después de eso la baja es lo correcto, sigue; 📣 dirección recibe aviso para mantener trazabilidad.',
        buttonTrue: '🗑️ Dar de baja',
        buttonFalse: 'Seguir intentando cerrar'
    },
    {
        title: '📊 Esto se ve en los números',
        message:
            '🎯 No todos los pedidos se salvan, pero cada uno que eliminas es margen que no entra. Eso puede alejar al equipo de la meta del mes y de la utilidad que planeaban. Si estás seguro después de agotar opciones, confirma; 🔔 dirección recibe el parte.',
        buttonTrue: 'Eliminar pedido',
        buttonFalse: 'No — lo conservo'
    },
    {
        title: '🛡️ Última barrera antes de perderlo de verdad',
        message:
            '⚡ Una vez eliminado, este pedido ya no te empuja el cierre del mes. Piénsalo como una venta que en el corto plazo no recuperas. Avanza solo si ya intentaste cerrar por todos los medios que tenías. 📬 Dirección se entera al ejecutar.',
        buttonTrue: '🗑️ Eliminar de todos modos',
        buttonFalse: 'Parar acá — no eliminar'
    },
    {
        title: '🌐 La org necesita ver el pipeline',
        message:
            '🏛️ Cuando se pierde pipeline, tiene que saberse. Eliminar este pedido baja ingresos esperados y puede afectar objetivos. La acción se comunica a dirección para que todos decidan con datos. ¿Confirmas después de haber agotado alternativas?',
        buttonTrue: 'Sí, confirmo la baja',
        buttonFalse: 'No eliminar'
    },
    {
        title: '💡 ¿De verdad quieres convertir esto en venta perdida hoy?',
        message:
            '📅 El mes es corto; cada baja resta de lo que todavía puedes facturar. Si no hay ruta comercial real después de un cierre en serio, la eliminación puede ser válida. Ten presente: ✅ dirección se entera y el impacto se ve en los indicadores.',
        buttonTrue: '🗑️ Eliminar',
        buttonFalse: 'Darle más tiempo al pedido'
    },
    {
        title: '📉 Menos pipeline, más presión en la meta',
        message:
            '🔢 Cada pedido eliminado es historia de ingreso que cierra mal en el periodo. Eso tensiona metas y utilidad. Si ya recorriste opciones para salvar la venta y la baja es inevitable, puedes proceder; 📢 dirección se entera de la eliminación.',
        buttonTrue: 'Proceder',
        buttonFalse: 'Conservar el pedido'
    },
    {
        title: '🤝 Confianza y transparencia',
        message:
            '💬 Las buenas decisiones van con datos y con intención real de cerrar. Si después de intentarlo por todos lados razonables el pedido tiene que salir del sistema, hazlo sabiendo que 📋 dirección recibe aviso y esa venta no vuelve al mes en curso.',
        buttonTrue: '🗑️ Eliminar ya',
        buttonFalse: 'Mantener — todavía hay valor'
    }
];
